<div class="page-container">
  <div class="card">
    <h1 class="card-title">Log Merge</h1>
    <hr class="divider">
    <!-- Unified two-column layout -->
    <section class="form-section">
      <div class="form-grid">

        <!-- Left Column: Upload + Configuration -->
        <div class="form-column">
          <h2 class="section-title"><strong>Upload LAS Files</strong></h2>
          <div class="drop-zone"
               [class.disabled]="isProcessing"
               (dragover)="onDragOver($event)"
               (drop)="onDrop($event)">
            <div class="drop-zone-content">
              <svg width="28" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              <p>Drag & drop LAS files here, or</p>
              <label class="file-input-label" [class.disabled]="isProcessing">
                <!--
                  Clearing the input value on click guarantees a fresh
                  ``change`` event fires even when the user picks the same
                  files again. Without this, the browser suppresses the
                  event and the app keeps the previous session state,
                  which makes "reupload same LAS files" a no-op.
                -->
                <input type="file" multiple accept=".las"
                       [disabled]="isProcessing"
                       (click)="$any($event.target).value = null"
                       (change)="onFilesSelected($event)" hidden>
                <span class="btn btn-primary" [class.disabled]="isProcessing">Browse Files</span>
              </label>
              <p class="file-count" *ngIf="selectedFiles.length > 0">
                {{ selectedFiles.length }} file(s) selected
              </p>
            </div>
          </div>

          <h2 class="section-title" style="margin-top:20px"><strong>Configuration</strong></h2>

          <div class="form-group">
            <label class="form-label">Log Suite Config</label>
            <div class="path-input-row">
              <input type="text" class="form-input" [(ngModel)]="config.log_suite_folder"
                     [disabled]="isProcessing"
                     placeholder="Path to .ini config file">
              <label class="btn btn-secondary path-browse-btn" [class.disabled]="isProcessing">
                Browse File
                <input type="file" accept=".ini" hidden
                       [disabled]="isProcessing"
                       (click)="$any($event.target).value = null"
                       (change)="onLogSuiteFileSelected($event)">
              </label>
            </div>
            <span class="field-hint" *ngIf="selectedLogSuiteFile">Selected config: {{ selectedLogSuiteFile.name }}</span>
          </div>

          <div class="form-row">
            <div class="form-group half">
              <label class="form-label required">Minimum Step</label>
              <input type="number" class="form-input" [(ngModel)]="config.min_step"
                     [disabled]="isProcessing"
                     step="0.0001" min="0">
            </div>
            <div class="form-group half">
              <label class="form-label required">Null Value</label>
              <input type="text" class="form-input" [(ngModel)]="config.null_value"
                     [disabled]="isProcessing">
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">Filter Mnemonic</label>
            <input type="text" class="form-input" [(ngModel)]="config.filter_mnemonic"
                   [disabled]="isProcessing"
                   placeholder="Comma-separated list (optional)">
          </div>
        </div>

        <!-- Right Column: Options -->
        <div class="form-column options-column">
          <h2 class="section-title"><strong>Options</strong></h2>

          <div class="options-grid">
            <!-- Logging Options -->
            <div class="option-group">
              <h3 class="option-group-title">Logging Options</h3>
              <label class="checkbox-label" [class.disabled]="isProcessing">
                <input type="checkbox" [(ngModel)]="config.debug" [disabled]="isProcessing">
                <span class="checkbox-text">Debug</span>
              </label>
              <label class="checkbox-label" [class.disabled]="isProcessing">
                <input type="checkbox" [(ngModel)]="config.allow_errors" [disabled]="isProcessing">
                <span class="checkbox-text">Allow Errors</span>
              </label>
              <label class="checkbox-label" [class.disabled]="isProcessing">
                <input type="checkbox" [(ngModel)]="config.allow_flagged" [disabled]="isProcessing">
                <span class="checkbox-text">Allow Flagged</span>
              </label>
              <label class="checkbox-label" [class.disabled]="isProcessing"
                     title="Data-normalization switch (not a logging option). When ON: validates curve mnemonics against the log-suite dictionary (dropping unknown ones), converts units to standard, and merges curves by family. When OFF: no validation or conversion and curves merge by their local mnemonic.">
                <input type="checkbox" [(ngModel)]="config.log_suite" [disabled]="isProcessing">
                <span class="checkbox-text">Log Suite</span>
                <span class="field-help-icon" aria-hidden="true">&#9432;</span>
              </label>
            </div>

            <!-- Processing Options -->
            <div class="option-group">
              <h3 class="option-group-title">Log Merge Pipeline Stages</h3>
              <!--
                Linear pipeline: Clean -> Resample -> Combine -> Merge. Each
                stage reads the previous one's output, so checking a later
                stage auto-checks + locks the earlier ones (they are required
                prerequisites). Locked boxes show checked + disabled.
              -->
              <label class="checkbox-label" [class.disabled]="isStageLocked('clean')">
                <input type="checkbox" [ngModel]="config.clean"
                       [disabled]="isStageLocked('clean')"
                       (ngModelChange)="onStageToggle('clean', $event)">
                <span class="checkbox-text">Clean</span>
                <span class="stage-lock-hint" *ngIf="isStageLocked('clean') && !isProcessing">required</span>
              </label>
              <label class="checkbox-label" [class.disabled]="isStageLocked('resample')">
                <input type="checkbox" [ngModel]="config.resample"
                       [disabled]="isStageLocked('resample')"
                       (ngModelChange)="onStageToggle('resample', $event)">
                <span class="checkbox-text">Resample</span>
                <span class="stage-lock-hint" *ngIf="isStageLocked('resample') && !isProcessing">required</span>
              </label>
              <label class="checkbox-label" [class.disabled]="isStageLocked('combine')">
                <input type="checkbox" [ngModel]="config.combine"
                       [disabled]="isStageLocked('combine')"
                       (ngModelChange)="onStageToggle('combine', $event)">
                <span class="checkbox-text">Combine</span>
                <span class="stage-lock-hint" *ngIf="isStageLocked('combine') && !isProcessing">required</span>
              </label>
              <label class="checkbox-label" [class.disabled]="isProcessing">
                <input type="checkbox" [ngModel]="config.merge"
                       [disabled]="isProcessing"
                       (ngModelChange)="onStageToggle('merge', $event)">
                <span class="checkbox-text">Merge</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Upload Progress (direct browser -> Blob Storage) -->
    <section class="form-section" *ngIf="isUploading">
      <div class="progress-container">
        <div class="progress-bar">
          <div class="progress-fill" [style.width.%]="uploadPercent"></div>
        </div>
        <span class="progress-text">{{ uploadPercent }}%</span>
      </div>
      <p class="status-message">
        Uploading {{ uploadedFileCount }} / {{ uploadTotalFileCount }} file(s) to cloud storage
      </p>
    </section>

    <!-- Progress Bar -->
    <section class="form-section" *ngIf="isProcessing || progress === 100">
      <div class="progress-container">
        <div class="progress-bar">
          <div class="progress-fill" [style.width.%]="progress"></div>
        </div>
        <span class="progress-text">{{ progress }}%</span>
      </div>
      <p class="status-message">{{ statusMessage }}</p>
    </section>

    <!-- Action Buttons -->
    <section class="form-actions">
      <button class="btn btn-primary" (click)="startProcessing()" [disabled]="isStartProcessingDisabled">
        Start Processing
      </button>
      <button class="btn btn-danger" (click)="cancel()" *ngIf="isProcessing">
        Cancel
      </button>
      <button class="btn btn-primary" (click)="downloadResults()" 
              *ngIf="progress === 100 && !isProcessing"
              [disabled]="isDownloading">
        {{ isDownloading ? 'Downloading...' : 'Download Results' }}
      </button>
    </section>

    <!-- Status Message (when not processing) -->
    <p class="status-message" *ngIf="statusMessage && !isProcessing && progress !== 100">
      {{ statusMessage }}
    </p>

    <!-- Loading Modal for LAS Scraper -->
    <div class="scraper-modal-overlay" *ngIf="isPreflighting">
      <div class="scraper-modal loading-modal">
        <div class="loading-content">
          <div class="spinner-large"></div>
          <h2 class="loading-title">Validating LAS Files...</h2>
          <p class="loading-message">{{ statusMessage }}</p>
          <p class="loading-hint">This may take a moment for large datasets.</p>
        </div>
      </div>
    </div>

    <!-- Scraper Insights Modal -->
    <div class="scraper-modal-overlay" *ngIf="showScraperModal && preflightReport">
      <div class="scraper-modal">
        <h2 class="section-title"><strong>LAS Scraper Validation</strong></h2>

        <div class="preflight-summary-grid">
          <div class="preflight-card">
            <div class="preflight-card-top"><span>Total Files</span><strong>{{ preflightReport.summary.total_files }}</strong></div>
            <p class="preflight-card-desc">Number of .LAS files found in the uploaded folder.</p>
          </div>
          <div class="preflight-card">
            <div class="preflight-card-top"><span>Valid Files</span><strong>{{ preflightReport.summary.valid_files }}</strong></div>
            <p class="preflight-card-desc">Files successfully parsed without fatal errors.</p>
          </div>
          <div class="preflight-card">
            <div class="preflight-card-top"><span>Wells Detected</span><strong>{{ preflightReport.summary.wells_detected }}</strong></div>
            <p class="preflight-card-desc">Unique wells identified by their API/UWI header value.</p>
          </div>
          <div class="preflight-card">
            <div class="preflight-card-top"><span>Total Curves</span><strong>{{ preflightReport.summary.total_curves }}</strong></div>
            <p class="preflight-card-desc">Sum of all curve columns across every file (including DEPTH).</p>
          </div>
          <div class="preflight-card">
            <div class="preflight-card-top"><span>Unique Mnemonics</span><strong>{{ preflightReport.summary.unique_mnemonics }}</strong></div>
            <p class="preflight-card-desc">Distinct curve names across all files — indicates data variety.</p>
          </div>
          <div class="preflight-card">
            <div class="preflight-card-top"><span>Unit-conflict Mnemonics</span><strong>{{ preflightReport.summary.unit_conflict_mnemonics }}</strong></div>
            <p class="preflight-card-desc">Mnemonics with mismatched units across runs — may affect merge quality.</p>
          </div>
          <div class="preflight-card">
            <div class="preflight-card-top"><span>Multi-run Wells</span><strong>{{ preflightReport.summary.wells_with_multiple_files }}</strong></div>
            <p class="preflight-card-desc">Wells with more than one LAS file — candidates for splicing/merging.</p>
          </div>
          <div class="preflight-card">
            <div class="preflight-card-top"><span>Wells with Overlap</span><strong>{{ preflightReport.summary.wells_with_overlaps }}</strong></div>
            <p class="preflight-card-desc">Wells where depth ranges of different runs overlap — handled by LogMerge during combine.</p>
          </div>
        </div>

        <div class="preflight-warnings" *ngIf="preflightReport.warnings.length > 0">
          <h3>Warnings</h3>
          <ul>
            <li *ngFor="let warning of preflightReport.warnings">{{ warning }}</li>
          </ul>
        </div>

        <div class="form-actions">
          <button class="btn btn-primary" (click)="confirmScraperInsights()">Confirm</button>
          <button class="btn btn-danger" (click)="cancelScraperInsights()">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</div>
